import React, { useEffect, useState, useCallback, Component } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { getMe, setOnUnauthorized } from './api/apiClient';
import Navigation from './components/Navigation';
import Announcements from './components/Announcements';
import HomePage from './components/HomePage';
import Timesheet from './components/Timesheet';
import StaffDirectory from './components/StaffDirectory';
import AdminSettings from './components/AdminSettings';
import Login from './components/Login';
import SsoOnboarding from './components/SsoOnboarding';
import DailyCheckIn from './components/DailyCheckIn';
import BugReportButton from './components/BugReportButton';
import Calendar from './components/Calendar';
import BDHub, { EmailPage } from './components/BDHub';
import './App.css';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: '40px', fontFamily: 'sans-serif', textAlign: 'center' }}>
          <h2>Something went wrong</h2>
          <p style={{ color: '#c00', marginBottom: 16 }}>{this.state.error.message}</p>
          <button onClick={() => this.setState({ error: null })} style={{ padding: '8px 20px', cursor: 'pointer' }}>
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const AppShell = ({ currentUser, onSignOut, onUserUpdate }) => (
  <div className="App">
    <Navigation
      currentUser={currentUser}
      onSignOut={onSignOut}
      onUserUpdate={onUserUpdate}
      isAuthenticated={true}
    />

    <main className="main-container">
      <Routes>
        <Route path="/" element={<HomePage currentUser={currentUser} />} />
        <Route path="/announcements" element={
          <Announcements currentUser={currentUser} isAdmin={currentUser.isAdmin} />
        } />
        <Route path="/directory" element={
          <div className="content">
            <StaffDirectory currentUser={currentUser} />
          </div>
        } />
        <Route path="/timesheet" element={
          <div className="content">
            <Timesheet currentUser={currentUser} />
          </div>
        } />
        <Route path="/calendar" element={
          <div className="content">
            <Calendar currentUser={currentUser} />
          </div>
        } />
        {/* Admin Settings — Mythical Admin only. */}
        <Route path="/admin" element={
          <div className="content">
            {currentUser.isMythical
              ? <AdminSettings currentUser={currentUser} />
              : <p style={{ padding: '2rem' }}>Access denied.</p>}
          </div>
        } />
        {/* Email — moved out of the CRM tabs; Mythical Admin only. */}
        <Route path="/crm/email" element={
          currentUser.isMythical
            ? <EmailPage />
            : <div className="content"><p style={{ padding: '2rem' }}>Access denied.</p></div>
        } />
        {/* CRM — every staff member sees their own contacts; admins see all. */}
        <Route path="/crm" element={<BDHub currentUser={currentUser} />} />
        {/* Legacy per-CRM paths → the unified CRM */}
        <Route path="/crm/chris" element={<Navigate to="/crm" replace />} />
        <Route path="/bd" element={<Navigate to="/crm" replace />} />
      </Routes>
    </main>

    <footer className="footer">
      <p>&copy; 2026 Esbrook PC. All rights reserved.</p>
    </footer>
  </div>
);

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [ssoError, setSsoError] = useState('');
  const [ssoPending, setSsoPending] = useState(null);

  const handleSignOut = useCallback(() => {
    setCurrentUser(null);
    localStorage.removeItem('intranet_token');
    localStorage.removeItem('intranet_user');
  }, []);

  useEffect(() => {
    setOnUnauthorized(handleSignOut);

    // Coming back from a Microsoft sign-in redirect
    const hashMatch = window.location.hash.match(/sso_token=([^&]+)/);
    if (hashMatch) {
      localStorage.setItem('intranet_token', decodeURIComponent(hashMatch[1]));
      window.history.replaceState(null, '', window.location.pathname);
    }

    const pendingMatch = window.location.hash.match(/sso_pending=([^&]+)/);
    if (pendingMatch) {
      const nameMatch = window.location.hash.match(/sso_name=([^&]+)/);
      setSsoPending({
        token: decodeURIComponent(pendingMatch[1]),
        name: nameMatch ? decodeURIComponent(nameMatch[1]) : '',
      });
      window.history.replaceState(null, '', window.location.pathname);
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get('sso_error')) {
      setSsoError(params.get('sso_error'));
      window.history.replaceState(null, '', window.location.pathname);
    }

    if (!localStorage.getItem('intranet_token')) {
      setLoadingUser(false);
      return;
    }

    getMe()
      .then((res) => {
        setCurrentUser({
          id: res.data.id,
          name: res.data.name,
          email: res.data.email,
          avatar: res.data.avatar || null,
          isAdmin: res.data.isAdmin,
          isMythical: res.data.isMythicalAdmin,
          office: res.data.office || 'Chicago',
        });
      })
      .catch(() => handleSignOut())
      .finally(() => setLoadingUser(false));
  }, [handleSignOut]);

  const handleLogin = useCallback((user) => {
    const { token, ...profile } = user;
    // Normalize the mythical-admin flag to the `isMythical` key the CRM reads.
    if (profile.isMythicalAdmin !== undefined) profile.isMythical = profile.isMythicalAdmin;
    setCurrentUser(profile);
    localStorage.setItem('intranet_token', token);
  }, []);

  const handleUserUpdate = useCallback((updated) => {
    setCurrentUser(prev => ({ ...prev, ...updated }));
  }, []);

  if (loadingUser) return null;

  if (ssoPending) {
    return (
      <SsoOnboarding
        name={ssoPending.name}
        pendingToken={ssoPending.token}
        onComplete={(user) => { handleLogin(user); setSsoPending(null); }}
        onCancel={() => setSsoPending(null)}
      />
    );
  }

  if (!currentUser) return <Login initialError={ssoError} onLogin={handleLogin} />;

  return (
    <ErrorBoundary>
      <AppShell
        currentUser={currentUser}
        onSignOut={handleSignOut}
        onUserUpdate={handleUserUpdate}
      />
      <DailyCheckIn currentUser={currentUser} />
      <BugReportButton />
    </ErrorBoundary>
  );
}

export default App;
